import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { ClearEvaluationTransferFilter, EvaluationTransferFormStateModel, EvaluationTransferState, InitEvaluationTransfer, LoadEvaluationTransfer, PageEvaluationTransfer, QueryEvaluationTransfer, SetelectedEvaluations, SortEvaluationTransfer, SubmitEvaluationTransfer } from "./evaluation-transfer.view.state";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup } from "@angular/forms";
import { PageEvent } from "@angular/material/paginator";
import { FilterSort, OrganizationGroup, Sample } from "@core/data";
import { SelectionModel } from "@angular/cdk/collections";
import { debounceTime, takeUntil } from "rxjs/operators";
import { coerseDateProperty, NumberValidators } from "@core/utils";
import { SampleIndexQuery } from "../sample-index/sample-index.state-model";
import { Snackbar } from "@core/material";

@Component({
    selector: 'pv-evaluation-transfer-view',
    templateUrl: 'evaluation-transfer.view.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-evaluation-transfer-view'
    }
})
export class EvaluationTransferView implements OnInit, OnDestroy {

    @Select(EvaluationTransferState)
    state$: Observable<EvaluationTransferFormStateModel>;

    @Select(EvaluationTransferState.query)
    query$: Observable<SampleIndexQuery>;

    @Select(EvaluationTransferState.groups)
    groups$: Observable<OrganizationGroup[]>;

    @Select(EvaluationTransferState.sort)
    sort$: Observable<FilterSort>;

    @Select(EvaluationTransferState.redirect)
    redirect$: Observable<string>;

    orderControl = new FormControl('desc');
    columnControl = new FormControl('createdAt');
    sortFormGroup = new FormGroup({
        column: this.columnControl,
        order: this.orderControl,
    });

    scionCultivarsControl = new FormControl([]);
    rootstockCultivarsControl = new FormControl([]);
    sitesControl = new FormControl([]);
    birthWeekControl = new FormControl();
    birthYearControl = new FormControl();
    searchControl = new FormControl();
    scionLicenseeControl = new FormControl();
    rowIndexControl = new FormControl(null, [NumberValidators.rangeOrSet()]);
    positionIndexControl = new FormControl(null, [NumberValidators.rangeOrSet()]);
    groupKeyControl = new FormControl([]);
    destroyedControl = new FormControl();
    excludeYearControl = new FormControl();
    startEvalPeriodControl = new FormControl(null);
    endEvalPeriodControl = new FormControl(null);


    queryFormGroup = new FormGroup({
        search: this.searchControl,
        scionCultivars: this.scionCultivarsControl,
        rootstockCultivars: this.rootstockCultivarsControl,
        sites: this.sitesControl,
        birthYear: this.birthYearControl,
        birthWeek: this.birthWeekControl,
        scionLicensee: this.scionLicenseeControl,
        rowIndex: this.rowIndexControl,
        positionIndex: this.positionIndexControl,
        groupKey: this.groupKeyControl,
        hideDestroyed: this.destroyedControl,
        excludeYear: this.excludeYearControl,
        startEvalPeriod: this.startEvalPeriodControl,
        endEvalPeriod: this.endEvalPeriodControl,
    });

    selectedSampleKey: string;

    selection = new SelectionModel<string>(true, []);

    private _destroy$ = new Subject();

    constructor(
        private _store: Store,
        private _router: Router,
        private _snackbar: Snackbar,
        private _route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this._route.paramMap
            .subscribe(params => {
                const orgKey = params.get('orgKey');
                const sampleKey = params.get('sampleKey');

                this._store.dispatch(new InitEvaluationTransfer(orgKey, sampleKey));

            });

        this.selection.changed
            .pipe(takeUntil(this._destroy$), debounceTime(100))
            .subscribe(() => {
                this._store.dispatch(new SetelectedEvaluations(this.selection.selected));
            });

        // Ensures that selection on UI always matches the state
        this.state$.subscribe(data => {
            if (data == null) {
                this.selection.clear();
                return;
            }

            if (data.evalKeys.length == 0) {
                this.selection.clear();
                return;
            }

            data.evalKeys.forEach(key => this.selection.select(key));
        });

        // apply state changes to query form
        this.query$.pipe(takeUntil(this._destroy$))
            .subscribe(query =>
                this.queryFormGroup.patchValue(query, { emitEvent: false })
            );

        // apply state changes to sort form
        this.sort$.pipe(takeUntil(this._destroy$))
            .subscribe(sort =>
                this.sortFormGroup.patchValue(sort, { emitEvent: false })
            );

        // handle query changes
        this.queryFormGroup.valueChanges
            .pipe(debounceTime(300), takeUntil(this._destroy$))
            .subscribe(val => {
                let data: SampleIndexQuery = {
                    ...val,
                    startEvalPeriod: val.startEvalPeriod ? coerseDateProperty(val.startEvalPeriod) : null,
                    endEvalPeriod: val.endEvalPeriod ? coerseDateProperty(val.endEvalPeriod) : null,
                }

                this._store.dispatch(new QueryEvaluationTransfer(data));
            });

        // handle sort changes
        this.sortFormGroup.valueChanges
            .pipe(debounceTime(200), takeUntil(this._destroy$))
            .subscribe(val => {
                this._store.dispatch(new SortEvaluationTransfer(val.column, val.order));
            });

        this.redirect$.subscribe(redirect => {
            if (!redirect) return;

            const state: EvaluationTransferFormStateModel = this._store.selectSnapshot(EvaluationTransferState);

            if (redirect != state.sampleKey) this._snackbar.message("Evaluation data transferred");
            else this._snackbar.message("Error while transferring evaluation data");

            this._router.navigate([
                '/org',
                state.orgKey,
                'samples',
                state.sample.type,
                redirect
            ]);
        })
    }

    toggleSortOrder(event: MouseEvent) {
        event.stopPropagation();
        this.orderControl.setValue(this.orderControl.value === 'asc' ? 'desc' : 'asc');
    }

    paginate(page: PageEvent) {
        this._store.dispatch(new PageEvaluationTransfer(page.pageIndex, page.pageSize));
    }

    selectSample(key: string) {
        if (this.selectedSampleKey == key) return this.selectedSampleKey = null;
        this.selectedSampleKey = key;
    }

    resetFilter() {
        this._store.dispatch(new ClearEvaluationTransferFilter());
    }

    back(sample: Sample) {
        this._router.navigate([
            '/org',
            sample.ownerOrgKey,
            'samples',
            sample.type,
            sample.key
        ]);
    }

    attempt() {
        if (this.selection.selected.length == 0) return this._snackbar.error('No evaluations selected to transfer');
        if (!this.selectedSampleKey) return this._snackbar.error('No sample selected as target');

        this._store.dispatch(new SubmitEvaluationTransfer(this.selectedSampleKey));
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}